import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_LUXON_DATE_FORMATS, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatDateFormats } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { defaultDateFormat } from '@config';
import { DateInputModel, DateRangeInputModel, DateRangeValue, InputValidationErrors } from '@libs/form/models';
import { DateTime } from 'luxon';
import { ErrorDirective, InputAttributesDirective } from '../../../directives';
import { InputBaseComponent } from '../../../models';
import { FormInputDescriptionComponent } from '../../description';
import { InputProviderFactory } from '../ng-input-provider';

export const dateRangeFormats: MatDateFormats = {
  ...MAT_LUXON_DATE_FORMATS,
  display: {
    ...MAT_LUXON_DATE_FORMATS.display,
    dateInput: defaultDateFormat,
  },
};

@Component({
  selector: 'ui-form-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    // Don't provide NG_VALIDATORS here — Angular Material's date-range input now handles cross-field validation
    ...InputProviderFactory.getProviders(DateRangeComponent, true),
    provideLuxonDateAdapter(dateRangeFormats),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ErrorDirective,
    InputAttributesDirective,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormInputDescriptionComponent,
  ],
})
export class DateRangeComponent extends InputBaseComponent<DateRangeInputModel> implements OnInit, AfterViewInit {
  private pickerOpen: boolean = false;

  @Input() public data!: DateRangeInputModel;
  @Output() public datePickerToggleEvent: EventEmitter<void> = new EventEmitter<void>();

  // DateRangeComponent doesn't use a single valueAccessor - it implements ControlValueAccessor directly
  public valueAccessor!: never;

  public controlStart: FormControl<DateTime | null> = new FormControl<DateTime | null>(null);
  public controlEnd: FormControl<DateTime | null> = new FormControl<DateTime | null>(null);
  public rangeFormGroup: FormGroup = new FormGroup({ start: this.controlStart, end: this.controlEnd });
  public stateStart!: DateInputModel;
  public stateEnd!: DateInputModel;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.prepareStates();
    // Initialize the form controls with the current value
    this.writeValue(this.control.value as DateRangeValue);
  }

  public ngAfterViewInit(): void {
    if (!this.control.validator) {
      this.control.addValidators(this.validate.bind(this));
    }
  }

  public updateValidity(): void {
    this.controlStart.updateValueAndValidity();
    this.controlEnd.updateValueAndValidity();
    this.controlStart.markAsDirty();
    this.controlEnd.markAsDirty();
    this.onTouchedCallback();
  }

  public startChanged(event: MatDatepickerInputEvent<DateTime>): void {
    const currentValue: DateRangeValue = this.rangeFormGroup.value as DateRangeValue;

    this.emitValue({ ...currentValue, start: event.value });
  }

  public endChanged(event: MatDatepickerInputEvent<DateTime>): void {
    const currentValue: DateRangeValue = this.rangeFormGroup.value as DateRangeValue;

    this.emitValue({ ...currentValue, end: event.value });
  }

  public onPickerOpened(): void {
    this.pickerOpen = true;
  }

  public onPickerClosed(): void {
    this.pickerOpen = false;
    this.onChangeCallback(this.rangeFormGroup.value);
  }

  public onDatePickerToggleClicked(): void {
    this.datePickerToggleEvent.emit();
  }

  public override writeValue(value: DateRangeValue): void {
    const safeValue: DateRangeValue = value ?? { start: null, end: null };

    this.rangeFormGroup.setValue(safeValue, { emitEvent: false });
  }

  public override validate(): InputValidationErrors | null {
    return this.controlStart.errors ?? this.controlEnd.errors;
  }

  public override registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public override registerOnChange(fn: (_: unknown) => unknown): void {
    this.onChangeCallback = fn;
  }

  public override setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.rangeFormGroup.disable();
    } else {
      this.rangeFormGroup.enable();
    }
  }

  private emitValue(value: DateRangeValue): void {
    if (this.pickerOpen) {
      return;
    }

    this.onChangeCallback(value);
  }

  private prepareStates(): void {
    this.stateStart = {
      type: 'date',
      name: `${this.data.name}-from`,
      label: this.data.label + ' Von',
      value: this.data.value?.start,
      attributes: this.data.attributes,
    };

    this.stateEnd = {
      type: 'date',
      name: `${this.data.name}-to`,
      label: this.data.label + ' Bis',
      value: this.data.value?.end,
      attributes: this.data.attributes,
    };
  }
}
